/**
 * 2.0 app pass, Part 7: the onboarding restyle, behind `onboarding_2_0_v1`.
 *
 * ONE READ, THEN CONTEXT. The flag is read once, as a literal, in
 * `routes/onboarding.tsx` (`useServerFlag('onboarding_2_0_v1')`, the call site
 * `tests/config-flag-export-rake.test.js` derives its keys from) and handed down
 * through `OnboardingV2Provider`. Every shell and step component asks
 * `useOnboardingV2()`, a plain context read: no network, no store, nothing to
 * mock. With no provider above it the answer is false, which is also the answer
 * for every other mount of these components (Settings, the guardian home).
 *
 * `useOnboardingV2OrServerFlag()` is the one exception, for `CollectionBeat`:
 * the same component also mounts on `/plan/first-look` and `/plan/draft`, outside
 * this route and its provider, with its props unchanged. There the provider is
 * absent and the server flag decides.
 *
 * Flag off: nothing in the tree reads a true, so every component renders the
 * tree it rendered before this file existed.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useServerFlag } from '../../hooks/useServerFlag';

/** The attribute the restyled tree carries. The v2 rules in the step CSS
 *  modules are all written as `:global([data-onb2]) .x`, so with the flag off no
 *  selector in them can match anything. */
export const ONBOARDING_V2_ATTR = 'data-onb2';

const V2Context = createContext<boolean | null>(null);

/** Provided by `routes/onboarding.tsx` with the one flag read. */
export const OnboardingV2Provider = V2Context.Provider;

/** True only under a provider that says true. */
export function useOnboardingV2(): boolean {
  return useContext(V2Context) === true;
}

/** For the component that also mounts outside the onboarding route: the
 *  provider's answer when there is one, else the server flag. */
export function useOnboardingV2OrServerFlag(): boolean {
  const fromRoute = useContext(V2Context);
  const fromServer = useServerFlag('onboarding_2_0_v1');
  return fromRoute ?? fromServer;
}

/** The two slots of the docked action bar on phones. `null` means "no dock":
 *  desktop, the flag off, or a component rendered outside the shell. */
export type ActionDockSlots = {
  /** Where the primary button of the step is portaled to. */
  ctaEl: HTMLElement | null;
  /** Where the reason line over the button is portaled to. */
  noteEl: HTMLElement | null;
};

const DockContext = createContext<ActionDockSlots | null>(null);
export const ActionDockProvider = DockContext.Provider;
export function useActionDock(): ActionDockSlots | null {
  return useContext(DockContext);
}

/**
 * `html[data-type-scale="ax"]`, as `TypeRampRoot` writes it (Part 4, ruling 4:
 * one flag, from AX1 up). Read live, so a text-size change while the screen is
 * open moves the reason line without a reload. False where the ramp is off.
 */
export function useAxScale(): boolean {
  const read = (): boolean =>
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-type-scale') === 'ax';
  const [ax, setAx] = useState<boolean>(read);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return undefined;
    const sync = (): void => setAx(read());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-type-scale'],
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `read` is a pure reader
  }, []);
  return ax;
}

/**
 * The reason a Continue is waiting ("Pick a month and a year to continue.").
 *
 * Flag off, or off the phone dock: exactly the `<p>` the step rendered before,
 * where it rendered it, so the flag-off DOM is the old DOM. On the phone dock it
 * rides in the bar, over the button. From AX1 up (Part 4's single `ax` flag) the
 * bar holds only Continue and the reason goes back into the page, after the
 * answers.
 *
 * `reserve` (fix round 1, critic LOW 2): every sentence this step can show, so the
 * bar holds the room of the tallest while the line changes or goes quiet (the B471
 * rule, for the dock). The bar is pinned to the foot of the screen, so a line that
 * went away used to pull the bar's top down and, with the page scrolled to its end
 * (where the last answer is tapped), the page slid down under the finger. Each
 * sentence is drawn invisibly, in the same grid cell as the visible line, from an
 * attribute (no second copy of the text in the DOM, nothing a reader or a text query
 * can find). Children may be empty: the step keeps `BarNote` mounted and the line only
 * stops saying anything. Off the dock `reserve` is ignored, and an empty line draws
 * nothing with the flag on, or the empty `<p>` it always drew with the flag off.
 */
export function BarNote({
  className,
  children,
  reserve,
}: {
  className?: string;
  children: ReactNode;
  reserve?: readonly string[];
}) {
  const v2 = useOnboardingV2();
  const dock = useActionDock();
  const ax = useAxScale();
  if (!v2) return <p className={className}>{children}</p>;
  const empty = children === null || children === undefined || children === false || children === '';
  const note = empty ? null : (
    <p className={className} data-onb2-note="">
      {children}
    </p>
  );
  if (!dock || ax) return note;
  if (!dock.noteEl) return null;
  return createPortal(
    <>
      {note}
      {reserve?.map((text) => (
        <p key={text} aria-hidden="true" data-onb2-reserve={text} />
      ))}
    </>,
    dock.noteEl,
  );
}
